/* eslint-disable no-console, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, no-unused-vars */
import {
  getGenPromptContent,
  getGenPromptRecord,
  upsertGenPrompt,
  updateGenPromptModelPreset,
} from '../../../data/gen-prompt';
import { getModelPreset } from '../../../data/model-preset';
import { getShow, getAllShows } from '../../../data/show';
import { listStoriesByShowSlug } from '../../../data/story';
import { HostPersonaApi } from '../../../services/hostPersonaApi';
import { waitForJob } from '../../../services/queueJob';
import { getEdgeFunctionAuthHeaders } from '../../../services/supabase';
import type {
  PromptDebuggerShow,
  PromptTestResponse,
  ModelConfig,
  PromptConfig,
} from '../types';

// API service for prompt management functionality
export class PromptService {
  /**
   * Test a prompt using the queue-centric architecture.
   * Creates a queue job and polls for completion.
   *
   * This is a synchronous-style wrapper around the async queue pattern,
   * used for features like model comparison that need to wait for results.
   */
  static async testPrompt(
    promptId: string,
    content: string,
    modelName: string,
    parameters: Record<string, unknown>,
    notify?: (message: string, options?: { type: 'error' }) => void
  ): Promise<PromptTestResponse> {
    try {
      // Handle image generation separately
      if (
        parameters.isImageGeneration ||
        promptId === 'show_image' ||
        promptId === 'story_image'
      ) {
        const result = await PromptService.createImageJob(
          promptId,
          parameters as Record<string, unknown>
        );

        if (!result.success || !result.jobId) {
          return {
            success: false,
            error: result.error || 'Failed to create image job',
          };
        }

        // Poll for completion
        const output = await PromptService.waitForJobCompletion(
          result.jobId,
          2 * 60 * 1000
        );
        return { success: true, result: output };
      }

      // For text generation, create a queue job
      const result = await PromptService.createTextJob(
        promptId,
        content,
        modelName,
        parameters
      );

      if (!result.success || !result.jobId) {
        const errorMsg = result.error || 'Failed to create text generation job';
        notify?.(errorMsg, { type: 'error' });
        return { success: false, error: errorMsg };
      }

      console.log('🛰️ PromptService.testPrompt: Waiting for job', result.jobId);

      // Poll for completion (timeout: 2 minutes for text generation)
      const output = await PromptService.waitForJobCompletion(
        result.jobId,
        2 * 60 * 1000
      );

      console.log('🛰️ PromptService.testPrompt: Job completed', {
        jobId: result.jobId,
        output,
      });

      // Return the result
      return {
        success: true,
        result: output?.result || output,
      };
    } catch (error) {
      console.error('Prompt test failed:', error);
      const errorMsg =
        error instanceof Error ? error.message : 'Unknown error occurred';
      notify?.(errorMsg, { type: 'error' });
      return {
        success: false,
        error: errorMsg,
        rawResponse: JSON.stringify({ error: String(error) }, null, 2),
      };
    }
  }

  /**
   * Poll a queue job until it completes or times out.
   * Used by synchronous-style wrappers that need to wait for results.
   *
   * @param jobId The job ID to poll
   * @param timeout Timeout in milliseconds (default: 2 minutes)
   * @param pollInterval Polling interval in milliseconds (default: 1 second)
   * @returns The job output when complete
   */
  static async waitForJobCompletion(
    jobId: number,
    timeout: number = 120000,
    pollInterval: number = 1000
  ): Promise<any> {
    const result = await waitForJob(jobId, {
      timeoutMs: timeout,
      pollIntervalMs: pollInterval,
    });

    if (result.outcome === 'completed') {
      return result.job.output;
    }

    if (result.outcome === 'failed') {
      throw new Error(
        result.job.last_error || `Job ${jobId} ${result.job.status}`
      );
    }

    // timeout / poll_error / aborted
    throw new Error(result.error);
  }

  /**
   * Get current prompt configuration from database
   */
  static async getPromptConfig(promptId: string): Promise<PromptConfig | null> {
    try {
      const promptContent = await getGenPromptContent(promptId);

      if (!promptContent) {
        console.warn(`No prompt config found for ${promptId}`);
        return null;
      }

      return { content: promptContent.replace(/\\n/g, '\n') };
    } catch (error) {
      console.error('Failed to get prompt config:', error);
      return null;
    }
  }

  /**
   * Get prompt content string from database
   */
  static async getPromptContent(promptId: string): Promise<string | null> {
    try {
      const promptContent = await getGenPromptContent(promptId);

      if (!promptContent) {
        console.warn(`No prompt content found for ${promptId}`);
        return null;
      }

      // Normalize escaped newlines to actual newlines for display
      return promptContent.replace(/\\n/g, '\n');
    } catch (error) {
      console.error('Failed to get prompt content:', error);
      return null;
    }
  }

  /**
   * Save prompt content to database
   */
  static async savePromptConfig(
    promptId: string,
    config: PromptConfig
  ): Promise<boolean> {
    try {
      // Normalize content by replacing double-escaped newlines with proper newlines
      // This ensures prompts are stored correctly and prevents issues with template processing
      const normalizedContent = config.content.replace(/\\n/g, '\n');
      let modelPreset = 'claude-sonnet-latest';

      try {
        const existing = await getGenPromptRecord(promptId);
        modelPreset = existing.modelPreset || modelPreset;
      } catch (lookupError) {
        console.info(
          `Prompt ${promptId} not found, creating new entry.`,
          lookupError
        );
      }

      await upsertGenPrompt({
        key: promptId,
        prompt: normalizedContent,
        modelPreset,
      });

      return true;
    } catch (error) {
      console.error('Failed to save prompt config:', error);
      return false;
    }
  }

  /**
   * Get model configuration from database
   */
  static async getModelConfig(promptId: string): Promise<ModelConfig | null> {
    try {
      const promptRecord = await getGenPromptRecord(promptId);

      if (!promptRecord.modelPreset) {
        console.warn(`Prompt ${promptId} missing model preset configuration.`);
        return null;
      }

      const preset = await getModelPreset(promptRecord.modelPreset);

      if (!preset) {
        console.warn(`No model preset found for ${promptRecord.modelPreset}`);
        return null;
      }

      // Return the model preset as configText for the UI
      return {
        configText: JSON.stringify(
          {
            model: preset.key,
            name: preset.name,
            type: preset.type,
            endpoint: preset.endpoint,
            body: preset.body,
            response_paths: preset.responsePaths,
          },
          null,
          2
        ),
      };
    } catch (error) {
      console.error('Failed to get model config:', error);
      return null;
    }
  }

  /**
   * Save model configuration to database
   */
  static async saveModelConfig(
    promptId: string,
    config: ModelConfig
  ): Promise<boolean> {
    try {
      // Parse the configText as JSON
      let configValue;
      try {
        configValue = JSON.parse(config.configText);
      } catch (parseError) {
        throw new Error(`Invalid JSON configuration: ${parseError}`);
      }

      // Update the prompt to use the specified model preset
      if (configValue.model) {
        await updateGenPromptModelPreset(promptId, configValue.model);
      }

      // Note: Model preset configurations are managed through the gen_model_presets table
      // This method now just updates which preset the prompt uses
      return true;
    } catch (error) {
      console.error('Failed to save model config:', error);
      return false;
    }
  }

  /**
   * Get available shows for dropdown.
   * Resolves persona instructions.
   */
  static async getShows(): Promise<PromptDebuggerShow[]> {
    try {
      const { data: shows } = await getAllShows();

      // Fetch all personas once for efficient lookup
      const personas = await HostPersonaApi.getAll();
      const personaMap = new Map(personas.map(p => [p.key, p]));

      return shows.map(show => {
        // Resolve instructions: use persona if assigned, otherwise use custom instructions
        let scriptInstructions1 = show.scriptInstructions1 ?? undefined;
        let scriptInstructions2 = show.scriptInstructions2 ?? undefined;
        let { voiceInstructions1 } = show;
        let { voiceInstructions2 } = show;

        if (show.hostPersona1) {
          const persona = personaMap.get(show.hostPersona1);
          if (persona) {
            scriptInstructions1 = persona.scriptInstructions ?? undefined;
            voiceInstructions1 = persona.voiceInstructions;
          }
        }

        if (show.hostPersona2) {
          const persona = personaMap.get(show.hostPersona2);
          if (persona) {
            scriptInstructions2 = persona.scriptInstructions ?? undefined;
            voiceInstructions2 = persona.voiceInstructions;
          }
        }

        return {
          id: show.id,
          slug: show.slug,
          topic: show.topic,
          name: show.slug, // Name removed from shows - use slug for display
          description: show.description ?? undefined,
          taxonomy: show.taxonomy ?? undefined,
          itunesCategory: show.itunesCategory ?? undefined,
          itunesSubcategory: show.itunesSubcategory ?? undefined,
          style: show.style,
          hostPersona1: show.hostPersona1 ?? undefined,
          hostPersona2: show.hostPersona2 ?? undefined,
          dynamicsKey: show.dynamicsKey ?? undefined,
          voiceProfile1: show.voiceProfile1,
          voiceIndex1: show.voiceIndex1 ?? undefined,
          voiceInstructions1,
          voiceProfile2: show.voiceProfile2,
          voiceIndex2: show.voiceIndex2 ?? undefined,
          voiceInstructions2,
          scriptInstructions1,
          scriptInstructions2,
          musicPrompt: show.musicPrompt ?? undefined,
          imagePrompt: show.imagePrompt ?? undefined,
          audioLogoDesc: show.audioLogoDesc ?? undefined,
          storyScope: show.storyScope ?? undefined,
          titleInstructions: show.titleInstructions ?? undefined,
          tags: show.tags ?? undefined,
          lang: show.lang ?? undefined,
          genreId: show.genreId || show.genre?.id,
          genre: show.genre
            ? {
                id: show.genre.id,
                name: show.genre.name,
                instructions: show.genre.instructions,
                description: show.genre.description,
              }
            : undefined,
        };
      });
    } catch (error) {
      console.error('Failed to get shows:', error);
      return [];
    }
  }

  /**
   * Get available stories for a show
   */
  static async getStoriesForShow(
    showId: string
  ): Promise<
    Array<{
      id: string;
      slug?: string;
      title: string;
      topic?: string;
      brief?: string;
      takeaway?: string;
      taxonomy?: string;
      description?: string;
      script?: string;
      subtitle?: string;
      searchTerms?: string;
      imagePrompt?: string;
      outline?: string;
      // #766: the stored delivery spec + rolled form, so selecting an existing
      // story populates {{story-delivery-spec}} instead of leaving the prior
      // story's plan in place.
      deliverySpec?: unknown;
      formKey?: string;
    }>
  > {
    try {
      const show = await getShow(showId);
      const stories = await listStoriesByShowSlug(show.slug, 5);

      return stories.map(story => ({
        id: story.id,
        slug: story.slug ?? undefined,
        title: story.title,
        topic: story.topic ?? undefined,
        brief: story.brief,
        takeaway: story.takeaway ?? undefined,
        taxonomy: story.taxonomy ?? undefined,
        description: story.description ?? undefined,
        script: story.script ?? undefined,
        subtitle: (story.subtitle ?? undefined) as string | undefined,
        searchTerms: (story.searchTerms ?? undefined) as string | undefined,
        imagePrompt: (story.imagePrompt ?? undefined) as string | undefined,
        outline: (story.outline ?? undefined) as string | undefined,
        deliverySpec: (story as { deliverySpec?: unknown }).deliverySpec ?? undefined,
        formKey: (story as { formKey?: string }).formKey ?? undefined,
      }));
    } catch (error) {
      console.error('Failed to get stories:', error);
      return [];
    }
  }

  /**
   * Improve prompt using AI assistance.
   * Uses the queue-centric architecture with polling.
   */
  static async improvePrompt(
    currentPrompt: string,
    instructions: string,
    modelPresetId: string
  ): Promise<{ prompt: string; description: string } | null> {
    try {
      // Build the prompt for improvement
      const enhancedInstructions = `${instructions}\n\nIMPORTANT: Please respond with a JSON object containing prompt and description keys.`;
      const promptTemplate = `
      <promptImprovement>
        <currentPrompt>
        ${currentPrompt}
        </currentPrompt>
        <instructions>
        ${enhancedInstructions}
        </instructions>
        <responseFormat>
          <![CDATA[
          {
            "prompt": "the improved prompt text",
            "description": "a brief description of what was improved"
          }
          ]]>
        </responseFormat>
      </promptImprovement>`;

      // Create a text generation job via queue
      const result = await PromptService.createTextJob(
        'prompt_improvement',
        promptTemplate,
        modelPresetId,
        {}
      );

      if (!result.success || !result.jobId) {
        console.error('Failed to create prompt improvement job:', result.error);
        return null;
      }

      console.log('📝 Prompt improvement job created:', result.jobId);

      // Poll for completion (timeout: 2 minutes)
      const output = await PromptService.waitForJobCompletion(
        result.jobId,
        2 * 60 * 1000
      );

      console.log('📝 Prompt improvement job completed:', {
        jobId: result.jobId,
        output,
      });

      // Try to parse the response as JSON
      try {
        const resultText = output?.result || output;
        const parsed =
          typeof resultText === 'string' ? JSON.parse(resultText) : resultText;
        if (parsed.prompt && parsed.description) {
          return parsed;
        } else {
          // Fallback if JSON parsing doesn't work
          return {
            prompt: currentPrompt,
            description: 'Prompt improvement via queue needs refinement',
          };
        }
      } catch (parseError) {
        console.warn('Failed to parse improvement response:', parseError);
        return {
          prompt: currentPrompt,
          description: 'Prompt improvement response format needs adjustment',
        };
      }
    } catch (error) {
      console.error('Prompt improvement failed:', error);
      return null;
    }
  }

  /**
   * Create a text generation queue job and return the job ID.
   *
   * This uses the queue-centric architecture with callback pattern.
   * Calls the debug-gen-text Edge Function which has service role access.
   *
   * @param promptId The prompt ID (e.g., 'story_script', 'show_profile')
   * @param promptTemplate The prompt template text
   * @param modelPresetId The model preset ID to use
   * @param parameters Template parameters to substitute
   * @returns Job ID for real-time subscription
   */
  static async createTextJob(
    promptId: string,
    promptTemplate: string,
    modelPresetId: string,
    parameters: Record<string, unknown>
  ): Promise<{ success: boolean; jobId?: number; error?: string }> {
    try {
      // Convert parameters to string values for template substitution
      const templateVars: Record<string, string> = {};
      for (const [key, value] of Object.entries(parameters)) {
        templateVars[key] = String(value || '');
      }

      console.log('📝 Creating text generation queue job:', {
        promptId,
        modelPresetId,
        parameterCount: Object.keys(templateVars).length,
      });

      // Call the debug-gen-text Edge Function (has service role access)
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/debug-gen-text`,
        {
          method: 'POST',
          headers: await getEdgeFunctionAuthHeaders(),
          body: JSON.stringify({
            promptTemplate,
            modelPresetId,
            parameters: templateVars,
            promptId,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to create text generation queue job:', errorText);
        return {
          success: false,
          error: `Failed to create job: ${errorText}`,
        };
      }

      const data = await response.json();

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Failed to create job',
        };
      }

      console.log('✅ Text generation queue job created:', {
        jobId: data.result.jobId,
      });

      return {
        success: true,
        jobId: data.result.jobId,
      };
    } catch (error) {
      console.error('Failed to create text generation queue job:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Create an image generation queue job and return the job ID.
   *
   * This uses the queue-centric architecture with callback pattern.
   * Calls the debug-gen-image Edge Function which has service role access.
   *
   * @param genPromptId The prompt ID to use ('show_image' or 'story_image')
   * @param params All available parameters - the template will use what it needs
   * @returns Job ID for real-time subscription
   */
  static async createImageJob(
    genPromptId: string,
    params: Record<string, unknown>
  ): Promise<{ success: boolean; jobId?: number; error?: string }> {
    try {
      // Get the appropriate image prompt based on genPromptId
      const imagePrompt =
        genPromptId === 'story_image'
          ? params['story-image-prompt'] || params.imagePrompt || ''
          : params['show-image-prompt'] || params.imagePrompt || '';

      if (!imagePrompt || String(imagePrompt).trim().length === 0) {
        return {
          success: false,
          error: 'Missing required parameter: image prompt',
        };
      }

      console.log('🖼️ Creating image generation queue job:', {
        genPromptId,
        paramCount: Object.keys(params).length,
        imagePromptLength: String(imagePrompt).length,
      });

      // Call the debug-gen-image Edge Function (has service role access)
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/debug-gen-image`,
        {
          method: 'POST',
          headers: await getEdgeFunctionAuthHeaders(),
          body: JSON.stringify({
            genPromptId,
            params,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          'Failed to create image generation queue job:',
          errorText
        );
        return {
          success: false,
          error: `Failed to create job: ${errorText}`,
        };
      }

      const data = await response.json();

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Failed to create job',
        };
      }

      console.log('✅ Image generation queue job created:', {
        jobId: data.result.jobId,
      });

      return {
        success: true,
        jobId: data.result.jobId,
      };
    } catch (error) {
      console.error('Failed to create image generation queue job:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Create a TTS queue job and return the job ID.
   *
   * This uses the queue-centric architecture with callback pattern.
   * Calls the debug-gen-tts Edge Function which has service role access.
   *
   * @param parameters TTS parameters (script, voice profiles, etc.)
   * @returns Job ID for real-time subscription
   */
  static async createTTSJob(
    parameters: Record<string, unknown>
  ): Promise<{ success: boolean; jobId?: number; error?: string }> {
    try {
      // Extract TTS parameters with type safety
      const script = String(
        parameters['script-content'] ||
          parameters.text ||
          parameters.script ||
          ''
      );
      const voiceProfile1 = String(
        parameters['voice-profile-1'] ||
          parameters.voice1 ||
          parameters.voice ||
          parameters.voiceProfile1 ||
          ''
      );
      const voiceProfile2 = String(
        parameters['voice-profile-2'] ||
          parameters.voice2 ||
          parameters.voiceProfile2 ||
          ''
      );
      const voiceInstructions1 = String(parameters.voiceInstructions1 || '');
      const voiceInstructions2 = String(parameters.voiceInstructions2 || '');
      const voiceIndex1 =
        parameters.voiceIndex1 !== null && parameters.voiceIndex1 !== undefined
          ? Number(parameters.voiceIndex1)
          : 0;
      const voiceIndex2 =
        parameters.voiceIndex2 !== null && parameters.voiceIndex2 !== undefined
          ? Number(parameters.voiceIndex2)
          : 0;
      const modelPreset = String(parameters.modelPreset || 'elevenlabs-tts');

      if (!script || script.trim().length === 0) {
        return {
          success: false,
          error: 'Missing required parameter: script text content',
        };
      }

      if (!voiceProfile1 || voiceProfile1.trim().length === 0) {
        return {
          success: false,
          error: 'Missing required parameter: voice1 profile',
        };
      }

      console.log('🎙️ Creating TTS queue job:', {
        scriptLength: script.length,
        voiceProfile1,
        voiceProfile2: voiceProfile2 || null,
        voiceIndex1,
        voiceIndex2,
        modelPreset,
      });

      // Call the debug-gen-tts Edge Function (has service role access)
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/debug-gen-tts`,
        {
          method: 'POST',
          headers: await getEdgeFunctionAuthHeaders(),
          body: JSON.stringify({
            script,
            voiceProfile1,
            voiceProfile2: voiceProfile2 || undefined,
            voiceInstructions1: voiceInstructions1 || undefined,
            voiceInstructions2: voiceInstructions2 || undefined,
            voiceIndex1,
            voiceIndex2,
            modelPreset,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to create TTS queue job:', errorText);
        return {
          success: false,
          error: `Failed to create job: ${errorText}`,
        };
      }

      const data = await response.json();

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Failed to create job',
        };
      }

      console.log('✅ TTS queue job created:', { jobId: data.result.jobId });

      return {
        success: true,
        jobId: data.result.jobId,
      };
    } catch (error) {
      console.error('Failed to create TTS queue job:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Create a Show Announce queue job and return the job ID.
   *
   * This uses the queue-centric architecture with callback pattern.
   * Calls the debug-gen-show-announce Edge Function which has service role access.
   *
   * Similar to debug-gen-tts, this accepts UI parameters directly (showName, showAudioLogo)
   * instead of pulling from database based on showId.
   *
   * @param parameters Show Announce parameters (showName, showAudioLogo, model preset)
   * @returns Job ID for real-time subscription
   */
  static async createShowAnnounceJob(
    parameters: Record<string, unknown>
  ): Promise<{ success: boolean; jobId?: number; error?: string }> {
    try {
      // Extract Show Announce parameters from UI with type safety
      // Support both hyphenated keys (from availableParams) and camelCase (from parameters object)
      const showName = String(
        parameters['show-name'] || parameters.showName || ''
      );
      const showAudioLogo = String(
        parameters['show-audio-logo'] || parameters.showAudioLogo || ''
      );
      const voiceProfile = String(
        parameters['voice-profile-1'] || parameters.voiceProfile1 || ''
      );
      const modelPresetId = String(parameters.modelPreset || 'elevenlabs-tts');
      const lang = String(parameters.lang || 'en');

      // Validate required parameters
      if (!showName || showName.trim().length === 0) {
        return {
          success: false,
          error: 'Missing required parameter: show-name',
        };
      }

      if (!showAudioLogo || showAudioLogo.trim().length === 0) {
        return {
          success: false,
          error: 'Missing required parameter: show-audio-logo',
        };
      }

      if (!voiceProfile || voiceProfile.trim().length === 0) {
        return {
          success: false,
          error: 'Missing required parameter: voice-profile-1',
        };
      }

      console.log('📢 Creating Show Announce queue job:', {
        showName,
        showAudioLogo: `${showAudioLogo.substring(0, 50)}...`,
        voiceProfile,
        modelPresetId,
        lang,
      });

      // Call the debug-gen-show-announce Edge Function (has service role access)
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/debug-gen-show-announce`,
        {
          method: 'POST',
          headers: await getEdgeFunctionAuthHeaders(),
          body: JSON.stringify({
            showName,
            showAudioLogo,
            voiceProfile,
            modelPresetId,
            lang,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to create Show Announce queue job:', errorText);
        return {
          success: false,
          error: `Failed to create job: ${errorText}`,
        };
      }

      const data = await response.json();

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Failed to create job',
        };
      }

      console.log('✅ Show Announce queue job created:', {
        jobId: data.result.jobId,
      });

      return {
        success: true,
        jobId: data.result.jobId,
      };
    } catch (error) {
      console.error('Failed to create Show Announce queue job:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Create a Story Announce queue job and return the job ID.
   *
   * This uses the queue-centric architecture with callback pattern.
   * Calls the debug-gen-story-announce Edge Function which has service role access.
   *
   * Similar to debug-gen-show-announce, this accepts UI parameters directly (storyTitle, showName)
   * instead of pulling from database based on storyId.
   *
   * @param parameters Story Announce parameters (storyTitle, showName, voiceProfile, model preset)
   * @returns Job ID for real-time subscription
   */
  static async createStoryAnnounceJob(
    parameters: Record<string, unknown>
  ): Promise<{ success: boolean; jobId?: number; error?: string }> {
    try {
      // Extract Story Announce parameters from UI with type safety
      // Support both hyphenated keys (from availableParams) and camelCase (from parameters object)
      const storyTitle = String(
        parameters['story-title'] || parameters.storyTitle || ''
      );
      const showName = String(
        parameters['show-name'] || parameters.showName || ''
      );
      const voiceProfile = String(
        parameters['voice-profile-1'] || parameters.voiceProfile1 || ''
      );
      const modelPresetId = String(parameters.modelPreset || 'elevenlabs-tts');

      // Validate required parameters
      if (!storyTitle || storyTitle.trim().length === 0) {
        return {
          success: false,
          error: 'Missing required parameter: story-title',
        };
      }

      if (!showName || showName.trim().length === 0) {
        return {
          success: false,
          error: 'Missing required parameter: show-name',
        };
      }

      if (!voiceProfile || voiceProfile.trim().length === 0) {
        return {
          success: false,
          error: 'Missing required parameter: voice-profile-1',
        };
      }

      console.log('📢 Creating Story Announce queue job:', {
        storyTitle: `${storyTitle.substring(0, 50)}...`,
        showName,
        voiceProfile,
        modelPresetId,
      });

      // Call the debug-gen-story-announce Edge Function (has service role access)
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/debug-gen-story-announce`,
        {
          method: 'POST',
          headers: await getEdgeFunctionAuthHeaders(),
          body: JSON.stringify({
            storyTitle,
            showName,
            voiceProfile,
            modelPresetId,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to create Story Announce queue job:', errorText);
        return {
          success: false,
          error: `Failed to create job: ${errorText}`,
        };
      }

      const data = await response.json();

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Failed to create job',
        };
      }

      console.log('✅ Story Announce queue job created:', {
        jobId: data.result.jobId,
      });

      return {
        success: true,
        jobId: data.result.jobId,
      };
    } catch (error) {
      console.error('Failed to create Story Announce queue job:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Compare multiple models by running the same prompt against each one
   * @param promptId - The prompt ID (e.g., 'story_script', 'show_profile')
   * @param promptTemplate - The prompt template to test
   * @param modelKeys - Array of model preset keys to compare
   * @param parameters - Template parameters
   * @param sectionTitle - Title of the section for report metadata
   * @param notify - Optional notification callback
   * @returns Promise with comparison report
   */
  static async compareModels(
    promptId: string,
    promptTemplate: string,
    modelKeys: string[],
    parameters: Record<string, unknown>,
    sectionTitle: string,
    notify?: (message: string, options?: { type: 'error' }) => void
  ): Promise<import('../types').ModelComparisonReport> {
    const timestamp = new Date().toISOString();
    const results: import('../types').ModelComparisonResult[] = [];

    // Execute all model calls in parallel using Promise.allSettled
    const promises = modelKeys.map(async modelKey => {
      const startTime = Date.now();

      try {
        // Call testPrompt for each model with the actual promptId
        const response = await PromptService.testPrompt(
          promptId, // Use the actual section's promptId
          promptTemplate,
          modelKey,
          parameters,
          notify
        );

        const endTime = Date.now();
        const responseTime = endTime - startTime;

        // Get model name for display
        const modelPreset = await import('@musedrops/shared').then(m =>
          m.getModelPreset(modelKey)
        );
        const modelName = modelPreset?.name || modelKey;

        return {
          modelKey,
          modelName,
          success: response.success,
          result: response.result,
          error: response.error,
          responseTime,
        };
      } catch (error) {
        const endTime = Date.now();
        const responseTime = endTime - startTime;

        // Get model name for display even on error
        let modelName = modelKey;
        try {
          const modelPreset = await import('@musedrops/shared').then(m =>
            m.getModelPreset(modelKey)
          );
          modelName = modelPreset?.name || modelKey;
        } catch {
          // Ignore error, use modelKey as fallback
        }

        return {
          modelKey,
          modelName,
          success: false,
          error:
            error instanceof Error ? error.message : 'Unknown error occurred',
          responseTime,
        };
      }
    });

    // Wait for all promises to settle (both fulfilled and rejected)
    const settledResults = await Promise.allSettled(promises);

    // Extract results from settled promises
    for (const settled of settledResults) {
      if (settled.status === 'fulfilled') {
        results.push(settled.value);
      } else {
        // This shouldn't happen since we catch errors inside the promise
        // but handle it just in case
        results.push({
          modelKey: 'unknown',
          modelName: 'Unknown Model',
          success: false,
          error:
            settled.reason instanceof Error
              ? settled.reason.message
              : 'Promise rejected',
          responseTime: 0,
        });
      }
    }

    return {
      timestamp,
      promptTemplate,
      parameters,
      sectionTitle,
      results,
      promptId,
    };
  }
}
/* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any */
